import type { CPU, CPUSnapshot } from '../cpu/CPU';
import type { MMU, MMUSnapshot } from '../mmu/MMU';
import type { InterruptController, InterruptSnapshot } from '../interrupt/InterruptController';
import type { PeripheralManager, PeripheralSnapshot } from '../peripheral/PeripheralManager';
import type { ModelConfig } from '../../model/ModelConfig';

export interface ChipsetOptions {
  cpu: CPU;
  mmu: MMU;
  interruptController: InterruptController;
  peripheralManager: PeripheralManager;
  modelConfig: ModelConfig;
}

export interface ChipsetState {
  running: boolean;
  frequency: number;
  tickInterval: number;
  totalTicks: number;
  cpuCycleCount: number;
}

export interface ChipsetSnapshot {
  cpu: CPUSnapshot;
  mmu: MMUSnapshot;
  interrupt: InterruptSnapshot;
  peripherals: PeripheralSnapshot[];
  totalTicks: number;
  timestamp: number;
}

export class Chipset {
  readonly cpu: CPU;
  readonly mmu: MMU;
  readonly interruptController: InterruptController;
  readonly peripheralManager: PeripheralManager;
  private running: boolean = false;
  private totalTicks: number = 0;

  stepHook: (() => boolean) | null = null;

  constructor(options: ChipsetOptions) {
    this.cpu = options.cpu;
    this.mmu = options.mmu;
    this.interruptController = options.interruptController;
    this.peripheralManager = options.peripheralManager;
  }

  async init(): Promise<void> {}

  reset(): void {
    this.totalTicks = 0;
    this.cpu.reset();
    this.interruptController.clear();
    this.peripheralManager.resetAll();
  }

  destroy(): void {
    this.running = false;
    this.peripheralManager.destroyAll();
  }

  start(): void {
    this.running = true;
  }

  pause(): void {
    this.running = false;
  }

  step(): void {
    this.tick(1);
  }

  isRunning(): boolean {
    return this.running;
  }

  tick(count: number): void {
    for (let i = 0; i < count; i++) {
      this.peripheralManager.tickAll();

      const pending = this.interruptController.getPendingInterrupt();

      let skipCpu = false;
      const standbyPeripheral = this.peripheralManager.getPeripheral('StandbyControl');
      if (standbyPeripheral) {
        const standby = standbyPeripheral as unknown as { isStandby(): boolean; wakeUp(): void };
        if (standby.isStandby()) {
          skipCpu = true;
          if (pending !== null) {
            standby.wakeUp();
            skipCpu = false;
          }
        }
      }

      if (pending !== null) {
        this.cpu.raiseException(pending.vector, pending.priority);
      }

      this.peripheralManager.tickAfterInterrupts();

      if (this.running && !skipCpu) {
        this.cpu.step();
        if (this.stepHook && this.stepHook()) {
          this.running = false;
          break;
        }
      }

      this.totalTicks++;
    }
  }

  setSpeed(_speed: number): void {}

  getSpeed(): number {
    return 1.0;
  }

  getState(): ChipsetState {
    return {
      running: this.running,
      frequency: 2000000,
      tickInterval: 500,
      totalTicks: this.totalTicks,
      cpuCycleCount: this.cpu.getCycleCount(),
    };
  }

  saveSnapshot(): ChipsetSnapshot {
    return {
      cpu: this.cpu.saveSnapshot(),
      mmu: this.mmu.saveSnapshot(),
      interrupt: this.interruptController.saveSnapshot(),
      peripherals: this.peripheralManager.saveSnapshots(),
      totalTicks: this.totalTicks,
      timestamp: Date.now(),
    };
  }

  loadSnapshot(snapshot: ChipsetSnapshot): void {
    this.cpu.loadSnapshot(snapshot.cpu);
    this.mmu.loadSnapshot(snapshot.mmu);
    this.interruptController.loadSnapshot(snapshot.interrupt);
    this.peripheralManager.loadSnapshots(snapshot.peripherals);
    this.totalTicks = snapshot.totalTicks;
  }
}
